import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import axios from '../../../services/axios';
import Loading from '../../../components/Loading';
import PaginationBar from '../../../components/PaginationBar/PaginationBar';
import SectionMeta, { SectionHeaderRow } from '../../../components/SectionMeta';

import { Container } from '../../../styles/layout/Container';
import {
   PageHeader,
   PageHeaderText,
   PageTitle,
   PageSubtitle,
} from '../../../styles/layout/PageHeader';
import {
   Section,
   SectionBody,
   SectionHeader,
   SectionTitle,
} from '../../../styles/layout/Section';
import { FormGrid } from '../../../styles/layout/FormGrid';
import { Field } from '../../../styles/forms/Field';
import { ActionBar, ActionBarRight } from '../../../styles/layout/ActionBar';
import { TableWrapper } from '../../../styles/table/TableWrapper';
import { Table } from '../../../styles/table/Table';
import { EmptyState } from '../../../styles/feedback/EmptyState';

// ─── Mapeamentos de exibição ───────────────────────────────────────────────────

const EMPLOYMENT_STATUS_LABEL = {
   ATIVO: 'Ativo',
   EXPERIENCIA: 'Experiência',
   AFASTADO: 'Afastado',
   DESLIGADO: 'Desligado',
};

const ITEMS_PER_PAGE = 20;

function formatDateBR(isoDateString) {
   if (!isoDateString) return '—';
   const [year, month, day] = String(isoDateString).substring(0, 10).split('-');
   return `${day}/${month}/${year}`;
}

// ─── Componente ───────────────────────────────────────────────────────────────

export default function EmployeeList() {
   const navigate = useNavigate();

   // ─── Filtros ─────────────────────────────────────────────────────────────
   const [filterByName, setFilterByName] = useState('');
   const [filterByEmploymentStatus, setFilterByEmploymentStatus] =
      useState('ATIVO');
   const [filterByCompanyEntity, setFilterByCompanyEntity] = useState('');
   const [filterByDepartmentId, setFilterByDepartmentId] = useState('');
   const [filterByJobPositionId, setFilterByJobPositionId] = useState('');
   const [filterByHireDateFrom, setFilterByHireDateFrom] = useState('');
   const [filterByHireDateTo, setFilterByHireDateTo] = useState('');

   // ─── Dados da listagem ───────────────────────────────────────────────────
   const [employees, setEmployees] = useState([]);
   const [totalEmployeeCount, setTotalEmployeeCount] = useState(0);
   const [totalPages, setTotalPages] = useState(1);
   const [currentPage, setCurrentPage] = useState(1);
   const [isLoading, setIsLoading] = useState(false);
   const [sortConfig, setSortConfig] = useState({
      key: 'full_name',
      direction: 'ASC',
   });

   // ─── Dados auxiliares dos selects ────────────────────────────────────────
   const [availableDepartments, setAvailableDepartments] = useState([]);
   const [availableJobPositions, setAvailableJobPositions] = useState([]);

   // Carrega departamentos e cargos ao montar — necessários para os selects de filtro
   useEffect(() => {
      async function loadAuxiliarySelectData() {
         try {
            const [departmentsResponse, jobPositionsResponse] =
               await Promise.all([
                  axios.get('/hr/departments'),
                  axios.get('/hr/job-positions'),
               ]);
            setAvailableDepartments(
               departmentsResponse.data?.data?.departments ?? []
            );
            setAvailableJobPositions(
               jobPositionsResponse.data?.data?.job_positions ?? []
            );
         } catch {
            toast.error('Erro ao carregar filtros. Tente recarregar a página.');
         }
      }
      loadAuxiliarySelectData();
   }, []);

   // ─── Busca ───────────────────────────────────────────────────────────────

   async function fetchEmployees(
      pageNumber = 1,
      sortBy = sortConfig.key,
      sortDirection = sortConfig.direction
   ) {
      setIsLoading(true);
      try {
         const queryParams = {
            page: pageNumber,
            limit: ITEMS_PER_PAGE,
         };

         queryParams.sort_by = sortBy;
         queryParams.sort_direction = sortDirection;

         if (filterByName.trim()) queryParams.name = filterByName.trim();
         if (filterByEmploymentStatus)
            queryParams.employment_status = filterByEmploymentStatus;
         if (filterByCompanyEntity)
            queryParams.company_entity = filterByCompanyEntity;
         if (filterByDepartmentId)
            queryParams.department_id = filterByDepartmentId;
         if (filterByJobPositionId)
            queryParams.job_position_id = filterByJobPositionId;
         if (filterByHireDateFrom)
            queryParams.hire_date_from = filterByHireDateFrom;
         if (filterByHireDateTo) queryParams.hire_date_to = filterByHireDateTo;

         const response = await axios.get('/hr/employees', {
            params: queryParams,
         });

         setEmployees(response.data?.data?.employees ?? []);
         setTotalEmployeeCount(response.data?.meta?.total ?? 0);
         setTotalPages(response.data?.meta?.total_pages ?? 1);
         setCurrentPage(pageNumber);
      } catch (error) {
         const errorMessage =
            error?.response?.data?.errors?.[0] ??
            error?.message ??
            'Erro ao buscar funcionários.';
         toast.error(errorMessage);
      } finally {
         setIsLoading(false);
      }
   }

   function handleSearchFormSubmit(event) {
      event.preventDefault();
      fetchEmployees(1);
   }

   function handleClearFilters() {
      setFilterByName('');
      setFilterByEmploymentStatus('ATIVO');
      setFilterByCompanyEntity('');
      setFilterByDepartmentId('');
      setFilterByJobPositionId('');
      setFilterByHireDateFrom('');
      setFilterByHireDateTo('');
      setEmployees([]);
      setTotalEmployeeCount(0);
      setTotalPages(1);
      setCurrentPage(1);
   }

   function handleSortColumn(columnKey) {
      const newDirection =
         sortConfig.key === columnKey && sortConfig.direction === 'ASC'
            ? 'DESC'
            : 'ASC';
      setSortConfig({ key: columnKey, direction: newDirection });
      fetchEmployees(1, columnKey, newDirection);
   }

   function getSortIcon(columnKey) {
      if (sortConfig.key !== columnKey) return ' ⇅';
      return sortConfig.direction === 'ASC' ? ' ▲' : ' ▼';
   }

   // ─── Render ──────────────────────────────────────────────────────────────

   return (
      <Container>
         <Loading isLoading={isLoading} />

         <PageHeader>
            <PageHeaderText>
               <PageTitle>Funcionários</PageTitle>
               <PageSubtitle>
                  Cadastro e gestão de colaboradores do Grupo MyShop. Duplo
                  clique na linha para abrir a ficha.
               </PageSubtitle>
            </PageHeaderText>
         </PageHeader>

         {/* ─── Filtros ─────────────────────────────────────────── */}
         <Section>
            <SectionHeader>
               <SectionTitle>Pesquisa</SectionTitle>
            </SectionHeader>
            <SectionBody>
               <form onSubmit={handleSearchFormSubmit}>
                  <FormGrid $cols={12} $colsMobile={12}>
                     <Field $col={6} $colMobile={12}>
                        <label>Nome</label>
                        <input
                           type="text"
                           value={filterByName}
                           onChange={(e) => setFilterByName(e.target.value)}
                           placeholder="Buscar por nome..."
                        />
                     </Field>

                     <Field $col={3} $colMobile={6}>
                        <label>Status</label>
                        <select
                           value={filterByEmploymentStatus}
                           onChange={(e) =>
                              setFilterByEmploymentStatus(e.target.value)
                           }
                        >
                           <option value="">Todos</option>
                           <option value="ATIVO">Ativo</option>
                           <option value="EXP 1">Exp. 1 - 30d</option>
                           <option value="EXP 2">Exp. 2 - 31d - 90d</option>
                           <option value="AFASTADO">Afastado</option>
                           <option value="FÉRIAS">Férias</option>
                           <option value="DESLIGADO">Desligado</option>
                        </select>
                     </Field>

                     <Field $col={3} $colMobile={6}>
                        <label>Empresa</label>
                        <select
                           value={filterByCompanyEntity}
                           onChange={(e) =>
                              setFilterByCompanyEntity(e.target.value)
                           }
                        >
                           <option value="">Todas</option>
                           <option value="MYSHOP COMERCIO E SERVICOS LTDA">
                              MyShop Comércio e Serviços LTDA
                           </option>
                           <option value="LIFE PURIFICADORES LTDA">
                              Life Purificadores LTDA
                           </option>
                        </select>
                     </Field>

                     <Field $col={3} $colMobile={12}>
                        <label>Departamento</label>
                        <select
                           value={filterByDepartmentId}
                           onChange={(e) =>
                              setFilterByDepartmentId(e.target.value)
                           }
                        >
                           <option value="">Todos</option>
                           {availableDepartments.map((department) => (
                              <option key={department.id} value={department.id}>
                                 {department.department_name}
                              </option>
                           ))}
                        </select>
                     </Field>

                     <Field $col={3} $colMobile={12}>
                        <label>Cargo</label>
                        <select
                           value={filterByJobPositionId}
                           onChange={(e) =>
                              setFilterByJobPositionId(e.target.value)
                           }
                        >
                           <option value="">Todos</option>
                           {availableJobPositions.map((jobPosition) => (
                              <option
                                 key={jobPosition.id}
                                 value={jobPosition.id}
                              >
                                 {jobPosition.position_name}
                              </option>
                           ))}
                        </select>
                     </Field>
                     <Field $col={3} $colMobile={6}>
                        <label>Admissão de</label>
                        <input
                           type="date"
                           value={filterByHireDateFrom}
                           onChange={(e) =>
                              setFilterByHireDateFrom(e.target.value)
                           }
                        />
                     </Field>

                     <Field $col={3} $colMobile={6}>
                        <label>Admissão até</label>
                        <input
                           type="date"
                           value={filterByHireDateTo}
                           onChange={(e) =>
                              setFilterByHireDateTo(e.target.value)
                           }
                        />
                     </Field>

                     <Field $col={12} $colMobile={12} $alignEnd>
                        <label>&nbsp;</label>
                        <ActionBar>
                           <ActionBarRight>
                              <button
                                 type="button"
                                 onClick={handleClearFilters}
                                 disabled={isLoading}
                              >
                                 Limpar
                              </button>
                              <button type="submit" disabled={isLoading}>
                                 Pesquisar
                              </button>
                              <button
                                 type="button"
                                 onClick={() => navigate('/hr/employees/new')}
                                 disabled={isLoading}
                              >
                                 + Novo funcionário
                              </button>
                           </ActionBarRight>
                        </ActionBar>
                     </Field>
                  </FormGrid>
               </form>
            </SectionBody>
         </Section>

         {/* ─── Resultados ──────────────────────────────────────── */}
         <Section>
            <SectionHeader>
               <SectionHeaderRow>
                  <SectionTitle>Resultados</SectionTitle>
                  <SectionMeta
                     items={[
                        {
                           key: 'total',
                           label: 'Total:',
                           value: totalEmployeeCount,
                        },
                        {
                           key: 'page',
                           label: 'Página',
                           value: `${currentPage}/${totalPages || 1}`,
                        },
                        {
                           key: 'per_page',
                           label: 'Por página',
                           value: String(ITEMS_PER_PAGE),
                        },
                     ]}
                  />
               </SectionHeaderRow>
            </SectionHeader>

            <SectionBody>
               {employees.length > 0 ? (
                  <>
                     <TableWrapper>
                        <Table>
                           <thead>
                              <tr>
                                 <th>ID</th>
                                 <th
                                    onClick={() =>
                                       handleSortColumn('full_name')
                                    }
                                    style={{
                                       cursor: 'pointer',
                                       userSelect: 'none',
                                    }}
                                 >
                                    Nome{getSortIcon('full_name')}
                                 </th>
                                 <th>Cargo</th>
                                 <th>Praça/Cidade</th>
                                 <th
                                    onClick={() =>
                                       handleSortColumn('department')
                                    }
                                    style={{
                                       cursor: 'pointer',
                                       userSelect: 'none',
                                    }}
                                 >
                                    Departamento{getSortIcon('department')}
                                 </th>
                                 <th
                                    onClick={() =>
                                       handleSortColumn('hire_date')
                                    }
                                    style={{
                                       cursor: 'pointer',
                                       userSelect: 'none',
                                    }}
                                 >
                                    Data de admissão{getSortIcon('hire_date')}
                                 </th>
                                 <th
                                    onClick={() =>
                                       handleSortColumn('employment_status')
                                    }
                                    style={{
                                       cursor: 'pointer',
                                       userSelect: 'none',
                                    }}
                                 >
                                    Status{getSortIcon('employment_status')}
                                 </th>
                              </tr>
                           </thead>
                           <tbody>
                              {employees.map((employee) => (
                                 <tr
                                    key={employee.id}
                                    onDoubleClick={() =>
                                       navigate(`/hr/employees/${employee.id}`)
                                    }
                                    title="Duplo clique para abrir a ficha"
                                 >
                                    <td style={{ textAlign: 'center' }}>
                                       {employee.id}
                                    </td>
                                    <td>{employee.full_name}</td>
                                    <td>
                                       {employee.jobPosition?.position_name ??
                                          '—'}
                                    </td>
                                    <td>{employee.city_location ?? '—'}</td>
                                    <td>
                                       {employee.department?.department_name ??
                                          '—'}
                                    </td>
                                    <td style={{ textAlign: 'center' }}>
                                       {formatDateBR(employee.hire_date)}
                                    </td>
                                    <td>
                                       {EMPLOYMENT_STATUS_LABEL[
                                          employee.employment_status
                                       ] ?? employee.employment_status}
                                    </td>
                                 </tr>
                              ))}
                           </tbody>
                        </Table>
                     </TableWrapper>

                     <PaginationBar
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={(page) => fetchEmployees(page)}
                        disabled={isLoading}
                     />
                  </>
               ) : (
                  <EmptyState
                     title="Nenhum funcionário encontrado"
                     description="Ajuste os filtros e clique em Pesquisar."
                  />
               )}
            </SectionBody>
         </Section>
      </Container>
   );
}
